import { IProject, ProjectRatings } from './projects.interface';
import { IFillProjSkillsOpt, SortBy, SortOrder, Context, IFillProjSkillsSortOpt } from './options.interface';
import { setLanguageStyle, unescapeHTMLEntities, formatDate, getYear } from './utils';
import { createTooltips, initReadMore, goToSection } from './global';
import { skillsSetup } from './skills';
import { Toast } from 'bootstrap';

export const checkMark = '<i class="fa-solid fa-circle-check"></i>';


/**
 * Compares two dates based on the specified order.
 * Treats invalid dates (e.g., "Present") as the most recent.
 * 
 * @param a First Date to compare
 * @param b Second Date to compare
 * @param asc If order is ascending
 * @returns Comparison result for sorting
 */
function compareDates(a: Date, b: Date, asc: boolean): number {
	const isValidA = !isNaN(a.getTime());
	const isValidB = !isNaN(b.getTime());

	if (!isValidA && !isValidB) return 0;
	if (!isValidA) return asc ? 1 : -1; // "Present" is treated as more recent
	if (!isValidB) return asc ? -1 : 1;

	const diff = asc ? (a.getTime() - b.getTime()) : (b.getTime() - a.getTime());
	if (diff === 0) {
		return 0;
	}
	return diff < 0 ? -1 : 1;
}


/**
 * Sorts projects based on the provided sortBy and sortOrder options.
 * 
 * @param projects Array of projects to sort
 * @param sortBy Criterion to sort by (StartDate or EndDate)
 * @param sortOrder Order to sort (Ascending or Descending)
 * @returns Sorted array of projects
 */
function sortProjects(projects: Readonly<IProject[]>, sortBy: SortBy, sortOrder: SortOrder): IProject[] {
	const asc = sortOrder === SortOrder.Ascending;
	return [...projects].sort((a, b) => {
		let comparison = 0;
		if (sortBy === SortBy.StartDate) {
			const dateA = new Date(a.start_date);
			const dateB = new Date(b.start_date);
			comparison = compareDates(dateA, dateB, asc);
		} else if (sortBy === SortBy.EndDate) {
			const dateA = new Date(a.end_date);
			const dateB = new Date(b.end_date);
			comparison = compareDates(dateA, dateB, asc);
			if (comparison === 0) {
				// If end dates are the same, sort by start date
				const startA = new Date(a.start_date);
				const startB = new Date(b.start_date);
				comparison = compareDates(startA, startB, asc);
			}
		}
		return comparison;
	});
}

/**
 * Filters projects based on context options.
 * 
 * @param projects Array of projects to filter
 * @param options Filtering options
 * @returns Filtered array of projects
 */
function filterProjectsByContext(projects: Readonly<IProject[]>, options: IFillProjSkillsOpt): IProject[] {
	if (!options.context || options.context.length === 0) return [...projects];
	return projects.filter(project => {
		return options.context!.some(c => {
			switch (c) {
				case Context.Academic:
					return !!project.university;
				case Context.Professional:
					return !!project.company;
				case Context.Personal:
					return !project.university && !project.company;
				default:
					return false;
			}
		});
	});
}

/**
 * Filters projects based on rating options.
 * 
 * @param projects Array of projects to filter
 * @param options Filtering options
 * @returns Filtered array of projects
 */
function filterProjectsByRating(projects: Readonly<IProject[]>, options: IFillProjSkillsOpt): IProject[] {
	if (!options.rating || options.rating.length === 0) return [...projects];
	return projects.filter(project => {
		return options.rating!.some(c => project.rating?.startsWith(c));
	});
}

/**
 * Filters projects based on technologies options.
 * 
 * @param projects Array of projects to filter
 * @param options Filtering options
 * @returns Filtered array of projects
 */
function filterProjectsByTechnologies(projects: Readonly<IProject[]>, options: IFillProjSkillsOpt): IProject[] {
	if (!options.technologies || options.technologies.length === 0) return [...projects];

	return projects.filter(project => {
		if (!project.technologies) return false;

		return options.technologies!.some(techOpt => {
			return project.technologies!.some(techProj => {
				const name = typeof techProj === 'object' ? techProj.name : techProj;

				// Special cases (to avoid conflicts)
				if (techOpt.toLowerCase() === 'c') {
					return name.toLowerCase() === 'c';
				}

				if (techOpt.toLowerCase() === 'java') {
					return name.toLowerCase() === 'java' || name.toLowerCase().startsWith('java ');
				}

				// General case: check if techOpt matches or starts the technology name
				return name.toLowerCase() === techOpt.toLowerCase() || name.toLowerCase().startsWith(techOpt.toLowerCase());
			});
		});
	});
}

/**
 * Sorts the array of projects according to the options given.
 * 
 * @param projects An array of projects.
 * @param options Options to sort and filter projects.
 * @returns The sorted array of projects according to the options.
 */
function sortProjectsAccordingToOptions(projects: Readonly<IProject[]>, options: IFillProjSkillsOpt): IProject[] {
	if (!options.sortBy) {
		options.sortBy = SortBy.EndDate;
	}
	if (!options.sortOrder) {
		options.sortOrder = SortOrder.Descending;
	}
	return sortProjects(projects, options.sortBy, options.sortOrder);
}

/**
 * Filters the array of projects according to the provided options.
 * 
 * @param projects An array of projects.
 * @param options Options to sort and filter projects.
 * @returns The filtered array of projects according to the options.
 */
function filterProjectsAccordingToOptions(projects: Readonly<IProject[]>, options: IFillProjSkillsOpt): IProject[] {
	let filtered = filterProjectsByContext(projects, options);
	filtered = filterProjectsByRating(filtered, options);
	filtered = filterProjectsByTechnologies(filtered, options);
	return filtered;
}

/**
 * Marks the selected dropdown items with a check mark.
 * 
 * @param dropdownId ID of the dropdown container
 * @param selectedItems Array of selected item identifiers
 * @param dataAttribute Data attribute used to identify items (optional)
 */
function markSelectedDropdownItems(
	dropdownId: string,
	selectedItems: string[],
	dataAttribute?: string
): void {
	const items = document.querySelectorAll<HTMLLinkElement>(`${dropdownId} .dropdown-item`);
	items.forEach(item => {
		item.innerHTML = item.innerHTML.replace(checkMark, ''); // Remove check marks from all items
		const itemData = dataAttribute ? item.getAttribute(`data-${dataAttribute}`) : item.textContent?.trim();
		if (itemData && selectedItems.includes(itemData)) {
			item.innerHTML += checkMark; // Add check mark to selected items
		}
	});
}

/**
 * Gets the selected sort options from the sort dropdown.
 * 
 * @returns The sort options as a serialized JSON object.
 */
function getSortDropdownSelected(): IFillProjSkillsSortOpt | undefined {
	const items = document.querySelectorAll<HTMLLinkElement>("#dropdown_sort_by .dropdown-item");
	for (const item of items) {
		if (item.innerHTML.includes(checkMark)) {
			return JSON.parse(item.dataset.sortby!);
		}
	}
	return undefined;
}

/**
 * Generates a unique ID for a project based on its name.
 * 
 * @param name The name of the project
 * @returns A sanitized and unique ID string
 */
export function generateProjectId(name: string): string {
	return name
		.replace(/c\+\+/gi, 'cpp')
		.replace(/\&ndash; /g, '')
		.replace(/\&mdash; /g, '')
		.replace(/"/g, '')
		.replace(/'/g, '')
		.replace(/@/g, '-')
		.replace(/\./g, '')
		.replace(/\(|\)/g, '')
		.toLowerCase()
		.trim()
		.replace(/ /g, "-");
}

/**
 * Generates the header for the projects section based on the provided options.
 * 
 * @param projects The array of projects to display.
 * @param options The sort and filtering options for the projects.
 * @returns The HTML string for the header.
 */
function generateHeader(projects: Readonly<IProject[]>, options: IFillProjSkillsOpt): void {
	const headerHTML = generateHeaderHTML(projects, options);
	const container = document.querySelector<HTMLDivElement>("#projectsHeader");
	if (container) {
		container.innerHTML = headerHTML;
	} else {
		console.warn("Projects header container not found.");
	}
}

/**
 * Generates the header for the projects section based on the provided options.
 * 
 * @param options The sort and filtering options for the projects.
 * @returns The HTML string for the header.
 */
function generateHeaderHTML(projects: Readonly<IProject[]>, options: IFillProjSkillsOpt): string {
	if ((!options.rating || options.rating.length === 0) &&
		(!options.context || options.context.length === 0) &&
		(!options.technologies || options.technologies.length === 0)) {
		return `<span class="content-title">ALL PROJECTS (${projects.length})</span>`;
	}

	const contentParts: string[] = [];
	if (options.rating && options.rating.length > 0) {
		contentParts.push(options.rating.sort().join(', '));
	}
	if (options.context && options.context.length > 0) {
		contentParts.push(options.context.sort().join(', '));
	}
	if (options.technologies && options.technologies.length > 0) {
		contentParts.push(options.technologies.sort().join(', '));
	}

	const headerContent = contentParts.join('<br/>');
	return `<span class="content-title">${headerContent} (${projects.length})</span>`;
}

function generateLegend() {
	const container = document.getElementById('projectLegend');
	if (!container) return;

	let legendHTML = '<span style="margin-right:10px;">Personal ratings:</span><div class="legend-container">';

	Object.keys(ProjectRatings).forEach(key => {
		if (key === '?') return; // Skip the unknown rating
		const { title, description, tooltip } = ProjectRatings[key];
		const ratingCSSClass = `rate${key}`;
		legendHTML += `
			<div class="legend-item">
				<span class="skillTooltip" data-bs-toggle="tooltip"
					data-bs-container="#projects" data-bs-trigger="hover" data-bs-placement="right"
					data-bs-html="true" data-bs-custom-class="skill-rating-tooltip"
					aria-label="Rating: ${key}"
					title="${tooltip}">
						<span class="badge ${ratingCSSClass}">${key}</span>
				</span>
				<span class="legend-title">${title}</span>
			</div>`;
	});

	legendHTML += '</div>';
	
	// Insert generated HTML into the container
	container.innerHTML = legendHTML;
}

/**
 * Generates the HTML for a university class.
 * 
 * @param name The name of the class.
 * @param url The URL of the class
 */
function generateUniversityClassHTML(name: string, url: string, style: string): string {
	return `<h4 class="project-class" style="${style}">
			<i class="fa-solid fa-book projectTooltip" aria-hidden="true" data-bs-toggle="tooltip"
			data-bs-placement="right" title="Class"></i>
			&nbsp;&nbsp;${unescapeHTMLEntities(name).toUpperCase()}
			${url ? `
				<a href="${url}" target="_blank" rel="noopener noreferrer">
					<i class="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i>
				</a>
			` : ''}
		</h4>`
}

/**
 * Generates the company or university section for a project.
 * 
 * @param project The project object
 * @returns The HTML string for the company or university section
 */
function generateCompanyOrUniversity(project: IProject): string {
	if (project.company) {
		return `
			<h4>
				<i class="fa-solid fa-building projectTooltip" aria-hidden="true" data-bs-toggle="tooltip"
					data-bs-placement="right" title="Company"></i>
				&nbsp;&nbsp;${project.company.toUpperCase()}
				${project.company_url ? `
					<a href="${project.company_url}" target="_blank" rel="noopener noreferrer">
						<i class="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i>
					</a>
				` : ''}
			</h4>`;
	} else if (project.university) {
		const classesHTML = Array.isArray(project.class)
			?
			project.class.map((cls, idx) => {
				let style = "";
				if (idx === 0 && 
						(project.class[idx].length + project.class[idx + 1].length < 80 
							|| project.name.length > 45)) {
					style = "clear:left;margin-right: 35px !important;";
				} else {
					style = "";
				}
				return generateUniversityClassHTML(cls,
					project.class_url && project.class_url[idx] ? project.class_url[idx] : undefined,
					style)
			}).join('')
			:
			generateUniversityClassHTML(project.class, project.class_url as string,
				(project.name.length > 45 ? "clear:left;margin-right: 35px !important;" : ""));
		return `
			<div class="clearfix">
				<h4 class="project-uni">
					<i class="fa-solid fa-building-columns projectTooltip" aria-hidden="true" data-bs-toggle="tooltip"
					data-bs-placement="right" title="University"></i>
					&nbsp;&nbsp;${unescapeHTMLEntities(project.university).toUpperCase()}
					${project.university_url ? `
						<a href="${project.university_url}" target="_blank" rel="noopener noreferrer">
							<i class="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i>
						</a>
					` : ''}
				</h4>
				${classesHTML}
			</div>`;
	}
	return '';
}

/**
 * Generates the technologies section for a project.
 * 
 * @param technologies The technologies used in the project
 * @returns The HTML string for the technologies section
 */
function generateTechnologies(technologies: (string | { name: string; linesOfCode: number })[] | undefined): string {
	if (!technologies || technologies.length === 0) return '';

	// Temporary container for measuring element widths
	const tempContainer = document.createElement('div');
	tempContainer.style.visibility = 'hidden';
	tempContainer.style.position = 'absolute';
	tempContainer.style.whiteSpace = 'nowrap';
	tempContainer.style.display = 'flex';
	document.body.appendChild(tempContainer);

	let totalWidth = 0;
	const maxWidth = 488; // Set your desired max width based on layout

	// Generate HTML for each technology item
	const techHTML = technologies.map((tech, index) => {
		const style = typeof tech === 'object' ? setLanguageStyle(tech.name) : setLanguageStyle(tech);
		const name = typeof tech === 'object' ? tech.name : tech;
		const tooltip = typeof tech === 'object' ? `LOC: ${tech.linesOfCode.toLocaleString()}` : '';

		// Append item temporarily to measure its width
		tempContainer.innerHTML = `<span style="${style}">${name}</span>`;
		const itemWidth = tempContainer.firstChild ? (tempContainer.firstChild as HTMLElement).offsetWidth : 0;

		// Determine if the item should be collapsible
		const isCollapsible = totalWidth + itemWidth > maxWidth;
		totalWidth += itemWidth;

		// Return HTML with or without the collapsible class
		return `<span style="${style}" ${tooltip ? `title="${tooltip}"` : ''} class="projectTooltip ${isCollapsible ? 'collapsible-content hide' : ''}">${name}</span>`;
	}).join('');

	// Clean up the temporary container
	document.body.removeChild(tempContainer);

	// HTML structure with a toggle button if any items are collapsible
	return `
		<div class="technologies">
			${techHTML}
			${totalWidth > maxWidth ?
				`<button class="toggle-btn" onclick="toggleTechnologies(this)">
					<i class="fas fa-angle-down"></i>
				</button>` : ''
			}
		</div>
	`;
}

/**
 * Toggles the visibility of collapsible technology items within a container.
 * 
 * @param button - The button element that triggers the toggle action.
 */
export function toggleTechnologies(button: HTMLButtonElement): void {
	const techContainer = button.parentElement;
	if (!techContainer) return;

	const collapsibleItems = techContainer.querySelectorAll('.collapsible-content') as NodeListOf<HTMLElement>;
	const isExpanded = Array.from(collapsibleItems).some(item => item.classList.contains('show-inline-block'));

	collapsibleItems.forEach(item => {
		item.classList.toggle('show-inline-block', !isExpanded);
		item.classList.toggle('hide', isExpanded);
	});

	const icon = button.querySelector('i');
	if (icon) {
		icon.classList.toggle('fa-angle-down', isExpanded);
		icon.classList.toggle('fa-angle-up', !isExpanded);
	}
}

// Attach the function to the global window object
(window as any).toggleTechnologies = toggleTechnologies;

/**
 * Generates the HTML for a project.
 * 
 * @param project A project object.
 * @returns The HTML string for the project.
 */
function generateProjectHTML(project: IProject): string {
	const startDateFull = formatDate(project.start_date);
	const endDateFull = formatDate(project.end_date);
	const startYear = getYear(project.start_date);
	const endYear = getYear(project.end_date);

	const ratingLetter = project.rating ? project.rating[0].toUpperCase() : '';
	const ratingCSSClass = project.rating ? `rate${ratingLetter === "?" ? "Unknown" : ratingLetter}` : "";
	const ratingTooltip = ratingLetter ? ProjectRatings[ratingLetter].tooltip : '';

	const id = generateProjectId(project.name);

	const generateIconClass = (url: string): string => {
		if (url.includes('github.com')) return 'fa-brands fa-github';
		if (url.includes('gitlab.com')) return 'fa-brands fa-gitlab';
		if (url.includes('bitbucket.org')) return 'fa-brands fa-bitbucket';
		return 'fa-solid fa-arrow-up-right-from-square';
	};

	const generateIconTitle = (url: string): string => {
		if (url.includes('github.com')) return 'GitHub';
		if (url.includes('gitlab.com')) return 'GitLab';
		if (url.includes('bitbucket.org')) return 'Bitbucket';
		return 'External Link';
	};

	const generateLinkHTML = (url: string): string => `
		<a href="${url}" target="_blank" rel="noopener noreferrer" class="project-link">
			<i class="${generateIconClass(url)} projectTooltip"
				aria-hidden="true" data-bs-toggle="tooltip"
				data-bs-container="#projects" data-bs-trigger="hover"
				data-bs-placement="top" title="${generateIconTitle(url)}"></i>
		</a>`;

	const projectUrlHTML = Array.isArray(project.url)
		? project.url.map(generateLinkHTML).join('')
		: project.url ? generateLinkHTML(project.url) : '';

	return `
		<div id="${id}" class="project-wrapper">
			<ul class="project">
				<li>
					<div class="project-tag">
						<i class="${project.fa_icon}" aria-hidden="true"></i>
						<div class="project-date">
							<span class="projectTooltip" data-bs-toggle="tooltip"
								data-bs-container="#projects" data-bs-trigger="hover" data-bs-placement="right"
								aria-label="Start date: ${startDateFull}" title="${startDateFull}">
								${startYear}
							</span>
							<div class="separator"></div>
							<span class="projectTooltip" data-bs-toggle="tooltip"
								data-bs-container="#projects" data-bs-trigger="hover" data-bs-placement="right"
								aria-label="End date: ${endDateFull}" title="${endDateFull}">
								${endYear}
							</span>
						</div>
					</div>
					<div class="timeline-item">
						<span class="timeline-location ${project.name.length > 45 ? "long-title" : ""}">
							<i class="fa-solid fa-location-dot" aria-hidden="true"></i> ${project.location.toUpperCase()}
						</span>
						<h3 class="timeline-header">
							<span class="projectTooltip" data-bs-toggle="tooltip"
								data-bs-container="#projects" data-bs-trigger="hover" data-bs-placement="right"
								data-bs-html="true" data-bs-custom-class="project-rating-tooltip"
								aria-label="Rating: ${project.rating}"
								title="${ratingTooltip}">
								<span class="badge bg-secondary ${ratingCSSClass}">${project.rating}</span>
							</span>
							<span class="project-title">${unescapeHTMLEntities(project.name).toUpperCase()}</span>
							${projectUrlHTML}
						</h3>
						<div class="timeline-body">
							${generateCompanyOrUniversity(project)}
							${generateTechnologies(project.technologies)}
							<div class="exp-description">${project.description}</div>
						</div>
					</div>
				</li>
			</ul>
		</div>`;
}

/**
 * Retrieves selected items from a dropdown based on a data attribute.
 * 
 * @param dropdownId ID of the dropdown container
 * @param dataAttribute Data attribute used to identify items (optional)
 * @returns An array of selected item values
 */
function getSelectedDropdownItems(dropdownId: string, dataAttribute?: string): string[] {
	const items = document.querySelectorAll<HTMLLinkElement>(`${dropdownId} .dropdown-item`);
	const selected: string[] = [];
	items.forEach(item => {
		if (item.innerHTML.includes(checkMark)) {
			const dataValue = dataAttribute ? item.getAttribute(`data-${dataAttribute}`) : item.textContent?.trim();
			if (dataValue) selected.push(dataValue);
		}
	});
	return selected;
}

/**
 * Gets the selected context options from the context dropdown.
 * 
 * @returns An array with context strings.
 */
function getContextDropdownSelected(): Context[] {
	return getSelectedDropdownItems("#dropdown_context") as Context[];
}

/**
 * Gets the selected rating options from the rating dropdown.
 * 
 * @returns An array with rating strings, from "A" to "F".
 */
function getRatingDropdownSelected(): string[] {
	return getSelectedDropdownItems("#dropdown_rating");
}

/**
 * Gets the selected technologies from the technologies dropdown.
 * 
 * @returns An array with technology strings.
 */
function getTechnologiesDropdownSelected(): string[] {
	return getSelectedDropdownItems("#dropdown_technologies", 'tech');
}

/**
 * Fills the empty options with default values if they are not provided.
 * 
 * @param options A possibly incomplete options object to be completed.
 * @returns The sorting and filtering options filled with default values.
 */
function fillEmptyOptionsWithDefault(options: IFillProjSkillsOpt): IFillProjSkillsOpt {
	const filledOptions: IFillProjSkillsOpt = { ...options };

	if (!filledOptions.sortBy || !filledOptions.sortOrder) {
		const sortOptions = getSortDropdownSelected();
		if (sortOptions) {
			filledOptions.sortBy = sortOptions.sortBy;
			filledOptions.sortOrder = sortOptions.sortOrder;
		} else {
			filledOptions.sortBy = SortBy.EndDate;
			filledOptions.sortOrder = SortOrder.Descending;
		}
	}

	if (!filledOptions.context) {
		filledOptions.context = getContextDropdownSelected();
	}
	if (!filledOptions.rating) {
		filledOptions.rating = getRatingDropdownSelected();
	}
	if (!filledOptions.technologies) {
		filledOptions.technologies = getTechnologiesDropdownSelected();
	}

	return filledOptions;
}

/**
 * Marks the selected sort option in the sort dropdown with a check mark.
 * 
 * @param options The sort and filtering options for the projects.
 */
function tickSortDropdownOption(options: IFillProjSkillsOpt): void {
	const sortBy = options.sortBy || SortBy.EndDate;
	const sortOrder = options.sortOrder || SortOrder.Descending;

	const items = document.querySelectorAll<HTMLLinkElement>("#dropdown_sort_by .dropdown-item");
	items.forEach(item => {
		item.innerHTML = item.innerHTML.replace(checkMark, '');
		const sortOption = JSON.parse(item.dataset.sortby!);
		if (sortOption.sortBy === sortBy && sortOption.sortOrder === sortOrder) {
			item.innerHTML += checkMark;
		}
	});
}

/**
 * Marks the selected rating options in the rating dropdown with a check mark.
 * 
 * @param options The sort and filtering options for the projects.
 */
function tickRatingDropdownOptions(options: IFillProjSkillsOpt): void {
	markSelectedDropdownItems("#dropdown_rating", options.rating || []);
}

/**
 * Marks the selected context options in the context dropdown with a check mark.
 * 
 * @param options The sort and filtering options for the projects.
 */
function tickContextDropdownOptions(options: IFillProjSkillsOpt): void {
	markSelectedDropdownItems("#dropdown_context", options.context?.map(c => c.toString()) || []);
}

/**
 * Marks the selected technology options in the technologies dropdown with a check mark.
 * 
 * @param options The sort and filtering options for the projects.
 */
function tickTechnologiesDropdownOptions(options: IFillProjSkillsOpt): void {
	markSelectedDropdownItems("#dropdown_technologies", options.technologies || [], 'tech');
}

/**
 * Receives an array with projects' data and fills the projects' section.
 * 
 * @param projects An Array with projects to fill the projects' section with.
 * @param options Options for filtering and sorting projects.
 */
export function fillProjectsSection(projects: Readonly<IProject[]>, options: IFillProjSkillsOpt) {
	// Fill empty options with defaults
	options = fillEmptyOptionsWithDefault(options);

	// Sort and filter projects
	let filteredProjects = sortProjectsAccordingToOptions(projects, options);
	filteredProjects = filterProjectsAccordingToOptions(filteredProjects, options);

	// Select container
	const container = document.querySelector<HTMLDivElement>("#projectsContainer");
	if (!container) {
		console.error("Projects container not found.");
		return;
	}

	// Generate HTML for all projects
	const projectsHTML = filteredProjects.map(project => generateProjectHTML(project)).join('');
	container.innerHTML = projectsHTML;

	// Generate header
	generateHeader(filteredProjects, options);

	// Mark selected dropdown items
	updateDropdowns(options);

	// Initialize tooltips and read more after the DOM update
	requestAnimationFrame(() => {
		createTooltips('.projectTooltip');
		initReadMore();
	});
}

/**
 * Updates all dropdowns with selected options by marking them with a check mark.
 * 
 * @param options The sort and filtering options for the projects.
 */
function updateDropdowns(options: IFillProjSkillsOpt): void {
	tickSortDropdownOption(options);
	tickRatingDropdownOptions(options);
	tickContextDropdownOptions(options);
	tickTechnologiesDropdownOptions(options);
}

function showHiddenProjectToast() {
	const toastEl = document.getElementById('toast-hidden-project');
	if (!toastEl) {
		console.error('Toast element not found');
		return;
	}

	const title = toastEl.querySelector(".toast-header .me-auto") as HTMLDivElement | null;
	const message = toastEl.querySelector(".toast-body") as HTMLDivElement | null;

	if (title && message) {
		title.innerText = "Project hidden";
		message.innerText = "This project is currently hidden due to the selected filtering options.";
	}

	// Display toast
	const toast = new Toast(toastEl);
	const toastContainer = toastEl.parentElement?.parentElement as HTMLElement | null;
	if (toastContainer) {
		toastContainer.style.display = "initial";
	}

	toast.show();

	// Event listener for hiding the toast
	toastEl.addEventListener('hidden.bs.toast', () => {
		if (toastContainer) {
			toastContainer.style.display = "none";
		}
	});
}

/**
 * Retrieve all the projects' data from the JSON file.
 * 
 * @returns {Promise<IProject[]>} An array with projects.
 */
async function loadProjectsJson(): Promise<IProject[]> {
	const url = 'json/projects.json?v=3';
	let response = await fetch(url);

	if (response.ok) {
		return await response.json();
	} else {
		throw new Error("Error fetching projects' data. Response status: " + response.status);
	}
}

/**
 * Make the text from a given HTMLElement's id to blink.
 * 
 * @param id The id of the HTMLElement.
 * @param durationMs The duration for the fadeOut and fadeIn animation, in milliseconds.
 * @param times How many times to fadeOut and fadeIn.
 */
function blinkText(id: string, durationMs=500, times=4) {
	for(var i=0; i < times; i++) {
		$(id).fadeOut(durationMs);
		$(id).fadeIn(durationMs);
	}
}

export function projectSetup() {
	// Mininum height for the projects article in order to accomodate the height of
	// the technologies' dropdown.
	const projectsContainer = document.querySelector('#projects .mCSB_container') as HTMLElement;

	if (projectsContainer) {
		projectsContainer.style.minHeight = '970px'; // Depends on tech dropdown size
	}

	// Focus search input
	document.querySelector('#dropdown_technologies')?.addEventListener('shown.bs.dropdown', () => {
		(document.querySelector('#dropdown_search_input') as HTMLInputElement)?.focus();
	});

	// Search a language
	document.querySelector('#dropdown_search_input')?.addEventListener('keyup', function () {
		const value = (this as HTMLInputElement).value.toLowerCase();
		const items = document.querySelectorAll("#dropdown_technologies .dropdown-item") as NodeListOf<HTMLAnchorElement>;

		items.forEach(item => {
			const itemText = item.textContent?.toLowerCase() || '';
			item.style.display = itemText.includes(value) ? 'block' : 'none';
		});
	});

	// Technologies selection
	document.querySelectorAll("#dropdown_technologies .dropdown-item").forEach(item => {
		item.addEventListener('click', function (e: MouseEvent) {
			const technologies: string[] = [];
			const items = document.querySelectorAll("#dropdown_technologies .dropdown-item") as NodeListOf<HTMLAnchorElement>;

			items.forEach(i => {
				if (i.innerHTML.includes(checkMark)) {
					technologies.push(i.dataset.tech as string);
				}
			});

			const tech = (this as HTMLAnchorElement).dataset.tech as string;
			if (technologies.includes(tech)) {
				technologies.splice(technologies.indexOf(tech), 1);
			} else {
				technologies.push(tech);
			}

			// Update the projects section with the new technologies filter
			fillProjectsSection(globalThis.projects, { technologies });

			e.preventDefault();
			e.stopPropagation();
		});
	});


	// Sort by
	document.querySelectorAll("#dropdown_sort_by .dropdown-item").forEach(item => {
		item.addEventListener('click', function (e: MouseEvent) {
			const sortBy = JSON.parse((this as HTMLAnchorElement).dataset.sortby || '{}');
			fillProjectsSection(globalThis.projects, sortBy);

			e.preventDefault();
			e.stopPropagation();
		});
	});

	// Rating
	document.querySelectorAll("#dropdown_rating .dropdown-item").forEach(item => {
		item.addEventListener('click', function (e: MouseEvent) {
			const rating: string[] = [];
			const items = document.querySelectorAll("#dropdown_rating .dropdown-item") as NodeListOf<HTMLAnchorElement>;

			items.forEach(i => {
				if (i.innerHTML.includes(checkMark)) {
					rating.push(i.innerHTML[0]);
				}
			});

			const classif = (this as HTMLAnchorElement).innerHTML[0];
			if (rating.includes(classif)) {
				rating.splice(rating.indexOf(classif), 1);
			} else {
				rating.push(classif);
			}

			fillProjectsSection(globalThis.projects, { rating });

			e.preventDefault();
			e.stopPropagation();
		});
	});

	// Context
	document.querySelectorAll("#dropdown_context .dropdown-item").forEach(item => {
		item.addEventListener('click', function (e: MouseEvent) {
			const context: Context[] = [];
			const items = document.querySelectorAll("#dropdown_context .dropdown-item") as NodeListOf<HTMLAnchorElement>;

			items.forEach(i => {
				if (i.innerHTML.includes(checkMark)) {
					context.push(i.dataset.context as Context);
				}
			});

			const ctx = (this as HTMLAnchorElement).dataset.context as Context;
			if (context.includes(ctx)) {
				context.splice(context.indexOf(ctx), 1);
			} else {
				context.push(ctx);
			}

			fillProjectsSection(globalThis.projects, { context });

			e.preventDefault();
			e.stopPropagation();
		});
	});

	/* Clicking on a link with class="anchor" will scroll to it with animation. */
	// Fix for binding to dynamic added elements: https://www.learningjquery.com/2017/02/jquery-on-method-the-issue-of-dynamically-added-elements
	$(document).on('click', '.anchor', function(e) {
		e.preventDefault(); // Don't change url

		const hash = $(this).attr('href');
		const linkEl = document.querySelector(hash);

		if (!linkEl) {
			showHiddenProjectToast();
			return;
		}
		if ($(window).width() < globalThis.mobileWidth) {
			$('html, body').animate({
				scrollTop: $(hash).offset().top - 15
			}, 700);
			blinkText(hash + ' h3');
		}
		else {
			goToSection('#projects', globalThis.articleSlideDurationMs);
			setTimeout(() => {
				$('#projects .panel-body').mCustomScrollbar('scrollTo', hash);
				blinkText(hash + ' h3');	
			}, globalThis.articleSlideDurationMs+10);
		}
		return false;
	});

	/// Load projects
	loadProjectsJson()
	.then((projects) => {
		generateLegend();
		globalThis.projects = projects.reverse();
		fillProjectsSection(projects,
			{sortBy: SortBy.EndDate, sortOrder: SortOrder.Descending, rating: ["A", "B"]});

		skillsSetup(projects);
	})
	.catch((e) => {
		console.error(e);
		var alert_html = '<div class="alert alert-danger">' +
			`<strong>Exception:</strong> ${e.message}</div>`;
		var container = document.querySelector("#projects #mCSB_5_container");
		let innerHTML = container.innerHTML;
		container.innerHTML = alert_html + innerHTML;
		container = document.querySelector("#skills #mCSB_4_container");
		innerHTML = container.innerHTML;
		container.innerHTML = alert_html + innerHTML;
	});

}